import type { DragEndEvent } from "@dnd-kit/core";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { twMerge } from "tailwind-merge";
import { Icon } from "@/components/Icon";
import { Skeleton } from "@/components/Skeleton";
import type { Column, SortConfig, TableDensity } from "./DataTable.types";

const densityClasses = {
  compact: "py-1.5 px-3 text-sm",
  default: "py-3 px-4 text-sm",
  relaxed: "py-4 px-4 text-base",
};

const alignmentClasses = {
  left: "text-left justify-start",
  center: "text-center justify-center",
  right: "text-right justify-end",
};

function getColumnStyles<T>(column: Column<T>) {
  const styles: React.CSSProperties = {};

  if (column.width !== undefined) {
    styles.width = typeof column.width === "number" ? `${column.width}px` : column.width;
  }
  if (column.minWidth !== undefined) {
    styles.minWidth = typeof column.minWidth === "number" ? `${column.minWidth}px` : column.minWidth;
  }
  if (column.maxWidth !== undefined) {
    styles.maxWidth = typeof column.maxWidth === "number" ? `${column.maxWidth}px` : column.maxWidth;
  }
  if (column.flex !== undefined) {
    styles.flex = `${column.flex} 1 0`;
  }

  return styles;
}

interface SortableHeaderCellProps<T> {
  column: Column<T>;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  isFirstColumn?: boolean;
  density?: TableDensity;
  isLoading?: boolean;
}

export function SortableHeaderCell<T>({
  column,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  isFirstColumn,
  density = "default",
  isLoading = false,
}: SortableHeaderCellProps<T>) {
  const isReorderable = column.reorderable !== false;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    disabled: !isReorderable || isLoading,
  });

  const isSorted = sortConfig?.columnId === column.id;
  const sortDirection = isSorted ? sortConfig.direction : null;
  const isSticky = stickyFirstColumn && isFirstColumn;

  const style: React.CSSProperties = {
    ...getColumnStyles(column),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.sortable ? "gap-2" : "gap-1",
        densityClasses[density],
        alignmentClasses[column.align || "left"],
        stickyHeader && "sticky top-0 z-10",
        isSticky && "sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        stickyHeader && isSticky && "z-30",
        column.sortable && !isLoading && "hover:bg-background-hover",
        isLoading && "cursor-not-allowed opacity-60",
        isSorted ? "font-bold text-text-body" : "font-medium text-text-muted",
        isDragging && "bg-background-hover"
      )}
      style={style}
      role="columnheader"
      aria-sort={isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
      aria-disabled={isLoading}
      {...attributes}
      {...listeners}
    >
      {isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <>
          <span
            className="flex-1 truncate cursor-pointer"
            onClick={() => !isLoading && column.sortable && onSort(column.id)}
          >
            {column.label}
          </span>
          {column.sortable && (
            <button
              type="button"
              className="flex flex-shrink-0 cursor-pointer"
              onClick={() => !isLoading && onSort(column.id)}
              aria-label={`Sort by ${column.label}`}
            >
              {isSorted ? (
                <Icon
                  name={sortDirection === "asc" ? "CaretUp" : "CaretDown"}
                  size={14}
                  weight="bold"
                  className="text-text-body"
                />
              ) : (
                <Icon name="CaretUpDown" size={12} className="text-text-muted opacity-40" />
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}

interface SortableHeaderRowProps<T> {
  columns: Column<T>[];
  columnOrder: string[];
  onColumnOrderChange: (newOrder: string[]) => void;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  density?: TableDensity;
  isLoading?: boolean;
}

export function SortableHeaderRow<T>({
  columns,
  columnOrder,
  onColumnOrderChange,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  density = "default",
  isLoading = false,
}: SortableHeaderRowProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map for quick column lookup
  const columnMap = new Map(columns.map((col) => [col.id, col]));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);

      // Check if the drop would displace a non-reorderable column
      const targetColumn = columnMap.get(over.id as string);
      if (targetColumn?.reorderable === false) {
        return;
      }

      // Prevent dropping before any non-reorderable columns
      // Find the first reorderable index (after all leading non-reorderable columns)
      let firstReorderableIndex = 0;
      for (let i = 0; i < columnOrder.length; i++) {
        const col = columnMap.get(columnOrder[i]);
        if (col?.reorderable === false) {
          firstReorderableIndex = i + 1;
        } else {
          break;
        }
      }

      if (newIndex < firstReorderableIndex) {
        return;
      }

      // Check if the move would displace any non-reorderable column
      // by checking if any locked column exists between old and new positions
      const minIndex = Math.min(oldIndex, newIndex);
      const maxIndex = Math.max(oldIndex, newIndex);
      for (let i = minIndex; i <= maxIndex; i++) {
        if (i === oldIndex) continue; // Skip the column being dragged
        const col = columnMap.get(columnOrder[i]);
        if (col?.reorderable === false) {
          return; // Abort if moving would displace a locked column
        }
      }

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onColumnOrderChange(newOrder);
    }
  };

  // Get ordered columns
  const orderedColumns = columnOrder
    .map((id) => columnMap.get(id))
    .filter((col): col is Column<T> => col !== undefined);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        <div className="contents" role="rowgroup">
          {orderedColumns.map((column, index) => (
            <SortableHeaderCell
              key={column.id}
              column={column}
              sortConfig={sortConfig}
              onSort={onSort}
              stickyHeader={stickyHeader}
              stickyFirstColumn={stickyFirstColumn}
              isFirstColumn={index === 0}
              density={density}
              isLoading={isLoading}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
